import { Spin } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { ReceiveRegionTopVo, SalesRegionVo, VisitorRegionVo } from "@zhihaoscm/entity";
import { useEffect, useMemo, useState } from "react";
import { Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";
import { AdminTableEmpty } from "@/views/components/TableEmpty";
import { areaLib } from "@/utils/areaLib";
import { useAreaLib } from "@/utils/hooks/useAreaLib";
import { formatNumber } from "@/utils/number";

import { ConsignmentSalesRegionChart, ConsignmentVisitorRegionChart } from "./ConsignmentGoodsAnalysisCharts";

export interface AudienceRegionParams {
    productId: string;
    areaLevel: number;
    beginTime?: string;
    endTime?: string;
}

interface RegionPanelState<T> {
    data: T[];
    loading: boolean;
    error: boolean;
}

export interface AudienceProfilePanelProps {
    productId: string;
    startDate: string;
    endDate: string;
    loadVisitorRegion: (params: AudienceRegionParams) => Promise<VisitorRegionVo[]>;
    loadSalesRegion: (params: AudienceRegionParams) => Promise<SalesRegionVo[]>;
    loadReceiveRegion?: (params: {
        productId: string;
        sortType: number;
        beginTime?: string;
        endTime?: string;
    }) => Promise<ReceiveRegionTopVo[]>;
}

const initialVisitorState: RegionPanelState<VisitorRegionVo> = { data: [], loading: true, error: false };
const initialSalesState: RegionPanelState<SalesRegionVo> = { data: [], loading: true, error: false };
const initialReceiveState: RegionPanelState<ReceiveRegionTopVo> = { data: [], loading: true, error: false };

/** 按旧 Vue 组件把行政区编码翻译为当前粒度的可见名称。 */
function getRegionName(areaName: string, areaLevel: number): string {
    const displayName = areaLevel === 1 ? areaLib.getFullAreaName(areaName) : areaLib.getCityNameByCode(areaName);
    return displayName || areaName || "--";
}

/** 缺省排行加载器使用稳定引用，避免面板每次重渲染都重复请求。 */
const loadEmptyReceiveRegion = async () => [] as ReceiveRegionTopVo[];

/** 旧 Vue 的两个地域图表各自维护省/市级别，保留独立键盘和鼠标选择。 */
function RegionLevelSelector({
    label,
    value,
    onChange
}: {
    label: string;
    value: number;
    onChange: (value: number) => void;
}) {
    return (
        <div className="audience-region-level-selector" role="group" aria-label={label}>
            {[
                { label: "省级", value: 1 },
                { label: "市级", value: 2 }
            ].map(option => (
                <button
                    key={option.value}
                    type="button"
                    className={value === option.value ? "active" : ""}
                    aria-pressed={value === option.value}
                    onClick={() => onChange(option.value)}
                >
                    {option.label}
                </button>
            ))}
        </div>
    );
}

function VisitorRegionPanel({
    state,
    onAreaLevelChange,
    areaLevel
}: {
    state: RegionPanelState<VisitorRegionVo>;
    onAreaLevelChange: (value: number) => void;
    areaLevel: number;
}) {
    return (
        <section className="audience-visitor-region-panel">
            <div className="audience-region-panel-heading">
                <h3>访客地域分布</h3>
                <RegionLevelSelector label="访客地域级别" value={areaLevel} onChange={onAreaLevelChange} />
            </div>
            {state.loading ? (
                <div className="audience-chart-loading" role="status">
                    <Spin aria-label="访客地域分布加载中" />
                </div>
            ) : state.error ? (
                <Alert type="error" message="访客地域分布加载失败" />
            ) : (
                <ConsignmentVisitorRegionChart data={state.data} />
            )}
        </section>
    );
}

function SalesRegionPanel({
    state,
    onAreaLevelChange,
    areaLevel
}: {
    state: RegionPanelState<SalesRegionVo>;
    onAreaLevelChange: (value: number) => void;
    areaLevel: number;
}) {
    return (
        <section className="audience-sales-region-panel">
            <div className="audience-region-panel-heading">
                <h3>销售情况地域分布</h3>
                <RegionLevelSelector label="销售地域级别" value={areaLevel} onChange={onAreaLevelChange} />
            </div>
            {state.loading ? (
                <div className="audience-chart-loading" role="status">
                    <Spin aria-label="销售情况地域分布加载中" />
                </div>
            ) : state.error ? (
                <Alert type="error" message="销售情况地域分布加载失败" />
            ) : (
                <ConsignmentSalesRegionChart data={state.data} />
            )}
        </section>
    );
}

function ReceiveRegionPanel({
    state,
    sortType,
    onSortTypeChange
}: {
    state: RegionPanelState<ReceiveRegionTopVo>;
    sortType: number;
    onSortTypeChange: (value: number) => void;
}) {
    const areaLibrary = useAreaLib();
    // 区域库异步加载完成后更新收货城市名称，排行数字格式沿用旧 Vue 的两位精度。
    const receiveColumns: TableColumnProps<ReceiveRegionTopVo>[] = [
        {
            label: "排名",
            minWidth: 150,
            prop: "rank",
            render: ({ rowIndex }) => (
                <span className={`audience-rank audience-rank-${rowIndex + 1}`}>{rowIndex + 1}</span>
            )
        },
        {
            label: "收货城市",
            minWidth: 250,
            prop: "cityName",
            render: ({ row }) =>
                areaLibrary.getShortAreaName(row.provinceName, row.cityName) || row.cityName || row.provinceName || "--"
        },
        { label: "订单量", minWidth: 200, prop: "orderCount" },
        {
            label: "下单吨数(吨)",
            minWidth: 180,
            prop: "orderTon",
            render: ({ row }) => (row.orderTon ? formatNumber(row.orderTon, 2) : "--")
        }
    ];

    return (
        <section className="audience-receive-region-panel">
            <div className="audience-region-panel-heading">
                <h3>收货地排名TOP5</h3>
                <div className="audience-region-sort-selector" role="group" aria-label="收货地排名排序">
                    {[
                        { label: "订单优先", value: 1 },
                        { label: "销量优先", value: 2 }
                    ].map(option => (
                        <button
                            key={option.value}
                            type="button"
                            className={sortType === option.value ? "active" : ""}
                            aria-pressed={sortType === option.value}
                            onClick={() => onSortTypeChange(option.value)}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            </div>
            {state.loading ? (
                <div className="audience-table-loading" role="status">
                    <Spin aria-label="收货地排名加载中" />
                </div>
            ) : state.error ? (
                <Alert type="error" message="收货地排名加载失败" />
            ) : state.data.length === 0 ? (
                <AdminTableEmpty />
            ) : (
                <ZhTable<ReceiveRegionTopVo>
                    aria-label="收货地排名TOP5"
                    rowKey={row => `${row.provinceName}-${row.cityName}`}
                    pagination={false}
                    data={state.data}
                    columns={receiveColumns}
                />
            )}
        </section>
    );
}

/** 受众画像沿用 Vue 的销售大图、访客图与收货排行布局和各自的筛选状态。 */
export function AudienceProfilePanel({
    productId,
    startDate,
    endDate,
    loadVisitorRegion,
    loadSalesRegion,
    loadReceiveRegion = loadEmptyReceiveRegion
}: AudienceProfilePanelProps) {
    const [visitorAreaLevel, setVisitorAreaLevel] = useState(1);
    const [salesAreaLevel, setSalesAreaLevel] = useState(1);
    const [receiveSortType, setReceiveSortType] = useState(2);
    const [visitorState, setVisitorState] = useState(initialVisitorState);
    const [salesState, setSalesState] = useState(initialSalesState);
    const [receiveState, setReceiveState] = useState(initialReceiveState);
    const visitorChartData = useMemo(
        () => visitorState.data.map(item => ({ ...item, areaName: getRegionName(item.areaName, visitorAreaLevel) })),
        [visitorAreaLevel, visitorState.data]
    );
    const salesChartData = useMemo(
        () => salesState.data.map(item => ({ ...item, areaName: getRegionName(item.areaName, salesAreaLevel) })),
        [salesAreaLevel, salesState.data]
    );

    // 访客级别独立刷新，和旧 Vue 访客地域子组件的 watch 依赖保持一致。
    useEffect(() => {
        let disposed = false;
        const params: AudienceRegionParams = {
            productId,
            areaLevel: visitorAreaLevel,
            beginTime: `${startDate}T00:00:00`,
            endTime: `${endDate}T23:59:59`
        };

        setVisitorState({ data: [], loading: true, error: false });
        void loadVisitorRegion(params)
            .then(data => {
                if (!disposed) setVisitorState({ data: data || [], loading: false, error: false });
            })
            .catch(() => {
                if (!disposed) setVisitorState({ data: [], loading: false, error: true });
            });

        return () => {
            disposed = true;
        };
    }, [endDate, loadVisitorRegion, productId, startDate, visitorAreaLevel]);

    // 销售级别独立刷新，切换访客级别时不会额外请求销售图表数据。
    useEffect(() => {
        let disposed = false;
        const params: AudienceRegionParams = {
            productId,
            areaLevel: salesAreaLevel,
            beginTime: `${startDate}T00:00:00`,
            endTime: `${endDate}T23:59:59`
        };

        setSalesState({ data: [], loading: true, error: false });
        void loadSalesRegion(params)
            .then(data => {
                if (!disposed) setSalesState({ data: data || [], loading: false, error: false });
            })
            .catch(() => {
                if (!disposed) setSalesState({ data: [], loading: false, error: true });
            });

        return () => {
            disposed = true;
        };
    }, [endDate, loadSalesRegion, productId, salesAreaLevel, startDate]);

    // 收货排行只监听日期和排序，保持旧 Vue 排行子组件的请求边界。
    useEffect(() => {
        let disposed = false;
        setReceiveState({ data: [], loading: true, error: false });
        void loadReceiveRegion({
            productId,
            sortType: receiveSortType,
            beginTime: `${startDate}T00:00:00`,
            endTime: `${endDate}T23:59:59`
        })
            .then(data => {
                if (!disposed) setReceiveState({ data: data || [], loading: false, error: false });
            })
            .catch(() => {
                if (!disposed) setReceiveState({ data: [], loading: false, error: true });
            });

        return () => {
            disposed = true;
        };
    }, [endDate, loadReceiveRegion, productId, receiveSortType, startDate]);

    return (
        <div className="audience-profile-panel" aria-label="受众画像地域分布">
            <SalesRegionPanel
                state={{ ...salesState, data: salesChartData }}
                areaLevel={salesAreaLevel}
                onAreaLevelChange={setSalesAreaLevel}
            />
            <div className="audience-profile-secondary-panels" data-testid="audience-profile-secondary-panels">
                <VisitorRegionPanel
                    state={{ ...visitorState, data: visitorChartData }}
                    areaLevel={visitorAreaLevel}
                    onAreaLevelChange={setVisitorAreaLevel}
                />
                <ReceiveRegionPanel
                    state={receiveState}
                    sortType={receiveSortType}
                    onSortTypeChange={setReceiveSortType}
                />
            </div>
        </div>
    );
}

export default AudienceProfilePanel;
